import { useMemo, useState } from "react";

import { Button, IconButton, Typography } from "@mui/material";
import moment from "moment";
import {
	FaPaperPlane,
	FaRegCommentDots,
	FaShareNodes,
	FaTrashCan,
} from "react-icons/fa6";

import {
	EHeightMeasurementUnit,
	ESpeedMeasurementUnit,
	TFlightLogComment,
	THeartRate,
} from "../../../../../@types";
import { useAccount } from "../../../../../hooks/useAccount";
import { convertHeight, convertSpeed } from "../../../../../utils";
import { TLocation } from "../FlightLogs";
import { toast } from "react-toastify";

type TChartDatum = {
	value: number;
};

type TFlightStorySectionsProps = {
	gpsData: TLocation[];
	heartRateData: THeartRate[];
	comments: TFlightLogComment[];
	addComment: (content: string) => Promise<void>;
	deleteComment: (commentId: number) => Promise<void>;
};

function MiniLineChart(props: {
	title: string;
	unit: string;
	data: TChartDatum[];
}) {
	const chart = useMemo(() => {
		if (props.data.length < 2) {
			return null;
		}

		const sampledData = props.data.filter((_, index) => {
			const step = Math.max(Math.ceil(props.data.length / 80), 1);
			return index % step === 0 || index === props.data.length - 1;
		});
		const values = sampledData.map(({ value }) => value);
		const min = Math.min(...values);
		const max = Math.max(...values);
		const range = Math.max(max - min, 1);
		const points = sampledData
			.map(({ value }, index) => {
				const x = (index / (sampledData.length - 1)) * 240;
				const y = 70 - ((value - min) / range) * 60;
				return `${x.toFixed(1)},${y.toFixed(1)}`;
			})
			.join(" ");

		return { min, max, points };
	}, [props.data]);

	if (!chart) {
		return null;
	}

	return (
		<div className="flight-story-chart">
			<div className="flight-story-chart-header">
				<Typography className="normal-text bold">{props.title}</Typography>
				<Typography className="small-text">
					{`${chart.max.toFixed(0)} ${props.unit} max`}
				</Typography>
			</div>
			<svg
				viewBox="0 0 240 80"
				role="img"
				aria-label={`${props.title} throughout the flight`}>
				<line x1="0" y1="10" x2="240" y2="10" className="chart-grid-line" />
				<line x1="0" y1="40" x2="240" y2="40" className="chart-grid-line" />
				<line x1="0" y1="70" x2="240" y2="70" className="chart-grid-line" />
				<polyline points={chart.points} className="chart-data-line" />
			</svg>
			<div className="flight-story-chart-range">
				<span>{`${chart.min.toFixed(0)} ${props.unit}`}</span>
				<span>Flight timeline</span>
			</div>
		</div>
	);
}

function getProfileName(comment: TFlightLogComment) {
	const firstName = comment.profile?.user?.firstName ?? "";
	const lastName = comment.profile?.user?.lastName ?? "";
	return `${firstName} ${lastName}`.trim() || "FlyORKA user";
}

export function FlightStorySections(props: TFlightStorySectionsProps) {
	const { account } = useAccount();
	const [commentText, setCommentText] = useState("");
	const [submittingComment, setSubmittingComment] = useState(false);
	const [shareFeedback, setShareFeedback] = useState("");

	const altitudeData = useMemo(
		() =>
			props.gpsData.map((point) => ({
				value: convertHeight(
					point.mslAltitude ?? point.altitude ?? 0,
					EHeightMeasurementUnit.METER,
					EHeightMeasurementUnit.FOOT,
				),
			})),
		[props.gpsData],
	);
	const speedData = useMemo(
		() =>
			props.gpsData.map((point) => ({
				value: convertSpeed(
					point.speed ?? 0,
					ESpeedMeasurementUnit.METERS_PER_SECOND,
					ESpeedMeasurementUnit.KNOTS,
				),
			})),
		[props.gpsData],
	);
	const heartRateData = useMemo(
		() => props.heartRateData.map(({ heartRate }) => ({ value: heartRate })),
		[props.heartRateData],
	);
	const averageAccuracy = useMemo(() => {
		if (props.gpsData.length === 0) {
			return null;
		}
		return (
			props.gpsData.reduce((sum, point) => sum + (point.accuracy ?? 0), 0) /
			props.gpsData.length
		);
	}, [props.gpsData]);

	const submitComment = async () => {
		const content = commentText.trim();
		if (!content || submittingComment) {
			return;
		}
		setSubmittingComment(true);
		try {
			await props.addComment(content);
			setCommentText("");
		} catch {
			toast.error("The comment could not be posted. Please try again.");
		} finally {
			setSubmittingComment(false);
		}
	};

	const shareFlight = async () => {
		const shareData = {
			title: "FlyORKA Flight Story",
			url: window.location.href,
		};
		try {
			if (navigator.share) {
				await navigator.share(shareData);
				setShareFeedback("Shared");
			} else {
				await navigator.clipboard.writeText(window.location.href);
				setShareFeedback("Link copied");
			}
		} catch {
			setShareFeedback("");
			toast.error("The flight link could not be shared.");
		}
	};

	const removeComment = async (commentId: number) => {
		try {
			await props.deleteComment(commentId);
		} catch {
			toast.error("The comment could not be deleted. Please try again.");
		}
	};

	const showAnalytics = altitudeData.length > 1 || speedData.length > 1;

	return (
		<>
			{showAnalytics && (
				<section
					className="flight-story-section"
					aria-labelledby="analytics-title">
					<div className="flight-story-section-header">
						<Typography id="analytics-title" className="normal-text bold">
							Flight analytics
						</Typography>
						{averageAccuracy !== null && (
							<span className="flight-story-quality-badge">
								{`${props.gpsData.length.toLocaleString()} GPS points · ${averageAccuracy.toFixed(0)} m avg accuracy`}
							</span>
						)}
					</div>
					<MiniLineChart title="Altitude" unit="ft" data={altitudeData} />
					<MiniLineChart title="Ground speed" unit="kt" data={speedData} />
					<MiniLineChart title="Heart rate" unit="BPM" data={heartRateData} />
				</section>
			)}

			<section
				className="flight-story-section"
				aria-labelledby="discussion-title">
				<div className="flight-story-section-header flight-story-discussion-header">
					<div className="flight-story-title-with-icon">
						<FaRegCommentDots aria-hidden />
						<Typography id="discussion-title" className="normal-text bold">
							Discussion
						</Typography>
						<span className="flight-story-comment-count">
							{props.comments.length}
						</span>
					</div>
					<div className="flight-story-share-container">
						{shareFeedback && <span>{shareFeedback}</span>}
						<IconButton
							size="small"
							title="Share this flight"
							aria-label="Share this flight"
							onClick={shareFlight}>
							<FaShareNodes />
						</IconButton>
					</div>
				</div>

				<div className="flight-story-comment-composer">
					<textarea
						value={commentText}
						maxLength={1000}
						rows={2}
						aria-label="Add to the discussion"
						placeholder="Add to the discussion…"
						onChange={(event) => setCommentText(event.target.value)}
					/>
					<Button
						className="flight-story-send-button"
						aria-label="Post comment"
						disabled={!commentText.trim() || submittingComment}
						onClick={submitComment}>
						<FaPaperPlane />
					</Button>
				</div>

				{props.comments.length === 0 && (
					<Typography className="small-text flight-story-empty-comments">
						No comments yet. Start the flight discussion.
					</Typography>
				)}
				<div className="flight-story-comment-list">
					{props.comments.map((comment) => {
						const isMyComment = account?.profile?.id === comment.profileId;
						const name = getProfileName(comment);
						return (
							<article key={comment.id} className="flight-story-comment">
								<div className="flight-story-comment-avatar" aria-hidden>
									{name.charAt(0).toUpperCase()}
								</div>
								<div className="flight-story-comment-body">
									<div className="flight-story-comment-meta">
										<strong>{name}</strong>
										<span>{moment(comment.createdAt).fromNow()}</span>
									</div>
									<Typography className="small-text" whiteSpace="break-spaces">
										{comment.content}
									</Typography>
								</div>
								{isMyComment && (
									<IconButton
										size="small"
										title="Delete comment"
										aria-label="Delete comment"
										onClick={() => removeComment(comment.id)}>
										<FaTrashCan />
									</IconButton>
								)}
							</article>
						);
					})}
				</div>
			</section>
		</>
	);
}
